import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight, Map as MapIcon, Grid3x3, SlidersHorizontal, X } from "lucide-react";
import AvailabilitiesMap from "@/components/AvailabilitiesMap";
import useSEO from "@/hooks/useSEO";

const STATUSES = [
  { value: "all", label: "Tous" },
  { value: "disponible", label: "Disponible" },
  { value: "reserve", label: "Réservé" },
  { value: "vendu", label: "Vendu" },
];

const formatPrice = (v) => (v ? new Intl.NumberFormat("fr-FR").format(v) + " DA" : "");

export default function Availabilities() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  useSEO({
    title: "Disponibilités — Logements à la réservation",
    description: "Découvrez les appartements et logements disponibles à la réservation chez AMAR SAHRAOUI Oran. Filtres par projet, type, surface, pièces et budget. Visite et information sur demande.",
    path: "/disponibilites",
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [roomsFilter, setRoomsFilter] = useState("all");
  const [maxBudget, setMaxBudget] = useState(60_000_000);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid");
  const gridRef = useRef(null);

  useEffect(() => {
    api.get("/public/availabilities").then((r) => setItems(r.data)).catch(() => {});
    api.get("/public/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const types = useMemo(() => Array.from(new Set(items.map((i) => i.type))).filter(Boolean), [items]);
  const roomOptions = useMemo(() => Array.from(new Set(items.map((i) => i.rooms))).filter(Boolean).sort(), [items]);
  const maxPrice = useMemo(() => Math.max(60_000_000, ...items.map((i) => i.price_value || 0)), [items]);

  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (projectFilter !== "all" && a.project_id !== projectFilter) return false;
      if (typeFilter !== "all" && a.type !== typeFilter) return false;
      if (roomsFilter !== "all" && String(a.rooms) !== String(roomsFilter)) return false;
      if (a.price_value && a.price_value > maxBudget) return false;
      if (search && !`${a.reference} ${a.project_name} ${a.surface}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [items, statusFilter, projectFilter, typeFilter, roomsFilter, maxBudget, search]);

  const handleMapProjectClick = (projectId) => {
    setProjectFilter(projectId);
    setView("grid");
    setTimeout(() => gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setProjectFilter("all");
    setTypeFilter("all");
    setRoomsFilter("all");
    setMaxBudget(maxPrice);
    setSearch("");
  };

  return (
    <div>
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16 border-b atelier-border-soft bg-atelier-deep relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-20" aria-hidden="true" />
        <div className="px-5 lg:px-10 relative z-10">
          <div className="label-tech text-atelier-ink mb-4">— Disponibilités</div>
          <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-none max-w-4xl text-atelier-text">
            Logements à <span className="italic text-atelier-ink">la réservation.</span>
          </h1>
        </div>
      </section>

      {/* Toolbar */}
      <section className="border-b atelier-border-soft bg-atelier-deep/80 backdrop-blur-md sticky top-16 z-30">
        <div className="px-5 lg:px-10 py-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-atelier-textFaint" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une référence, un projet…"
              data-testid="availabilities-search"
              className="atelier-input pl-10 w-full"
            />
          </div>

          <button onClick={() => setShowFilters(!showFilters)} className={`px-4 py-2 label-tech text-[0.65rem] border flex items-center gap-2 transition-all ${showFilters ? "bg-atelier-ink text-atelier-deep border-atelier-ink" : "atelier-border-soft text-atelier-textDim hover:border-atelier-ink hover:text-atelier-ink"}`} data-testid="availabilities-toggle-filters">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Filtres avancés
          </button>

          <div className="flex gap-1 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s.value}
                onClick={() => setStatusFilter(s.value)}
                data-testid={`availabilities-filter-status-${s.value}`}
                className={`px-3 py-2 label-tech text-[0.65rem] border transition-all duration-300 ${statusFilter === s.value ? "bg-atelier-ink text-atelier-deep border-atelier-ink" : "atelier-border-soft text-atelier-textDim hover:border-atelier-ink hover:text-atelier-ink"}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center atelier-border-soft">
            <button onClick={() => setView("grid")} className={`px-3 py-2 label-tech text-[0.65rem] flex items-center gap-2 transition-all ${view === "grid" ? "bg-atelier-ink text-atelier-deep" : "text-atelier-textDim hover:text-atelier-ink"}`} data-testid="availabilities-view-grid">
              <Grid3x3 className="w-3.5 h-3.5" /> Grille
            </button>
            <button onClick={() => setView("map")} className={`px-3 py-2 label-tech text-[0.65rem] flex items-center gap-2 transition-all border-l atelier-border-soft ${view === "map" ? "bg-atelier-ink text-atelier-deep" : "text-atelier-textDim hover:text-atelier-ink"}`} data-testid="availabilities-view-map">
              <MapIcon className="w-3.5 h-3.5" /> Carte
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="border-t atelier-border-soft bg-atelier-surface/40">
            <div className="px-5 lg:px-10 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="label-tech text-atelier-textFaint mb-2 block">Projet</label>
                <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="atelier-select w-full" data-testid="availabilities-filter-project">
                  <option value="all">Tous les projets</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-tech text-atelier-textFaint mb-2 block">Type de bien</label>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="atelier-select w-full" data-testid="availabilities-filter-type">
                  <option value="all">Tous les types</option>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label-tech text-atelier-textFaint mb-2 block">Pièces</label>
                <select value={roomsFilter} onChange={(e) => setRoomsFilter(e.target.value)} className="atelier-select w-full" data-testid="availabilities-filter-rooms">
                  <option value="all">Toutes</option>
                  {roomOptions.map((r) => <option key={r} value={r}>{r} pièces</option>)}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label-tech text-atelier-textFaint">Budget max</label>
                  <span className="text-xs text-atelier-text font-serif">{formatPrice(maxBudget)}</span>
                </div>
                <input
                  type="range"
                  min={10_000_000}
                  max={maxPrice}
                  step={1_000_000}
                  value={maxBudget}
                  onChange={(e) => setMaxBudget(Number(e.target.value))}
                  className="w-full accent-atelier-ink"
                  style={{ accentColor: "#b6c6f1" }}
                  data-testid="availabilities-filter-budget"
                />
              </div>
              <button onClick={resetFilters} className="label-tech text-atelier-ink hover:text-atelier-text flex items-center gap-1 col-span-2 md:col-span-4 justify-end" data-testid="availabilities-reset">
                <X className="w-3 h-3" /> Réinitialiser les filtres
              </button>
            </div>
          </div>
        )}
      </section>

      {/* MAP VIEW */}
      {view === "map" && (
        <section className="py-8 bg-atelier-deep">
          <div className="px-5 lg:px-10">
            <div className="atelier-border p-2 bg-atelier-surface/30">
              <AvailabilitiesMap projects={projects} availabilities={filtered} onProjectClick={handleMapProjectClick} />
            </div>
            <div className="mt-4 label-tech text-atelier-textFaint">
              {filtered.length} disponibilité(s) — Cliquez sur un marqueur pour voir les biens du projet correspondant.
            </div>
          </div>
        </section>
      )}

      {/* GRID VIEW */}
      <section ref={gridRef} className="py-12 lg:py-16 bg-atelier">
        <div className="px-5 lg:px-10">
          <div className="label-tech text-atelier-textFaint mb-6">{filtered.length} résultat(s)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => (
              <div key={a.id} className="atelier-border bg-atelier-surface/30 p-6 hover:border-atelier-ink/60 transition-all duration-400 scan-card flex flex-col" data-testid={`avail-${a.reference}`}>
                {a.image_url && (
                  <div className="aspect-[4/3] overflow-hidden bg-atelier-surface mb-5 proj-img-shell tech-corners">
                    <img src={a.image_url} alt={a.reference} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="label-tech text-atelier-ink mb-1">{a.project_name}</div>
                    <div className="font-serif text-2xl text-atelier-text">{a.reference}</div>
                  </div>
                  <span className={`pill ${a.status === "disponible" ? "text-atelier-ink" : a.status === "reserve" ? "text-amber-400" : "text-atelier-textFaint"}`}>
                    {a.status === "disponible" ? "Disponible" : a.status === "reserve" ? "Réservé" : "Vendu"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm text-atelier-textDim mt-4 mb-4">
                  <div><span className="label-tech text-[0.55rem] text-atelier-textFaint">Type</span><div className="text-atelier-text font-serif">{a.type}</div></div>
                  <div><span className="label-tech text-[0.55rem] text-atelier-textFaint">Surface</span><div className="text-atelier-text font-serif">{a.surface}</div></div>
                  {a.rooms ? <div><span className="label-tech text-[0.55rem] text-atelier-textFaint">Pièces</span><div className="text-atelier-text font-serif">{a.rooms}</div></div> : null}
                  {a.floor ? <div><span className="label-tech text-[0.55rem] text-atelier-textFaint">Étage</span><div className="text-atelier-text font-serif">{a.floor}</div></div> : null}
                  {a.orientation ? <div><span className="label-tech text-[0.55rem] text-atelier-textFaint">Orientation</span><div className="text-atelier-text font-serif">{a.orientation}</div></div> : null}
                  {a.price ? <div><span className="label-tech text-[0.55rem] text-atelier-textFaint">Prix</span><div className="text-atelier-ink font-serif font-medium">{a.price}</div></div> : null}
                </div>
                {a.description && <p className="text-sm text-atelier-textDim mb-4 flex-1 font-serif">{a.description}</p>}
                <Link to={`/contact?ref=${encodeURIComponent(a.reference)}&projet=${encodeURIComponent(a.project_name)}`} className="inline-flex items-center gap-2 label-tech text-atelier-ink border-b border-atelier-ink/40 pb-1 hover:border-atelier-ink transition self-start" data-testid={`avail-cta-${a.reference}`}>
                  Demander des informations <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full atelier-border border-dashed p-16 text-center text-atelier-textFaint">
                <span className="label-tech">Aucune disponibilité ne correspond à vos critères.</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
